import { useEffect, useMemo, useState } from "react";
import apiService from "services/api";
import { useNotificationContext } from "contexts";
import { useForm } from "react-hook-form";
import { PAGE, initialEventProp } from "./constants";
import { lessonCategories } from "pages/Course/constants";
import { LessonDetailPage } from "./LessonDetailPage";
import { LeaveOrOTDetailsPage } from "./LeaveOrOTDetailsPage";
import { AddAttendancePage } from "./AddAttendancePage";
import { useQuery } from "react-query";
import { transformData } from "./constants";
import { capitalize } from "utils/capitalize";
import { permissionCheck, models, permission } from "utils/permissionCheck";
import useLeanStudentQuery from "./queries";

const ViewEventModal = ({
	teacherOptions,
	open,
	hide,
	refetchEvents,
	eventProp = { id: "" },
	showStudentReportModal,
	showAddHomeworkModal,
	isLessonRecord = false,
}) => {
	const { createNotification } = useNotificationContext();
	const {
		handleSubmit,
		setValue,
		watch,
		register,
		reset,
		control,
		getValues,
		formState: { errors, dirtyFields },
	} = useForm({
		defaultValues: { studentIds: [], isCustomLesson: false },
	});

	const isAbleToCreateNewEvent = permissionCheck(
		apiService.auth.token.get(),
		models.LESSON_RECORD,
		[permission.CREATE, permission.ADMIN_EDIT_PERMISSION],
		"or",
	);

	const { data: studentOptions, isLoading: loadingStudents } = useQuery(
		["student"],
		() =>
			isAbleToCreateNewEvent
				? apiService.student
						.getAbsoluteMin(undefined, { show_deactivated: false })
						.then((data) =>
							data.map((c) => ({
								value: c.id,
								label:
									c.firstName +
									" " +
									c.lastName +
									" (" +
									c?.centre?.shortName +
									")",
								data: c,
								toString: () => c.id,
							})),
						)
				: Promise.resolve([]),
	);

	const [tempEvent, setTempEvent] = useState(initialEventProp);

	const parseDataToFormInput = (data) => setTempEvent(transformData(data));

	const {
		refetch: refetchSingleLessonRecord,
		isFetching: isFetchingSingleLessonRecord,
	} = useQuery(
		["lessonRecord", eventProp?.id],
		() => apiService.lessonRecord.get(eventProp?.id),
		{
			enabled: false,
			onSuccess: parseDataToFormInput,
		},
	);

	const [editable, setEditable] = useState(false);
	const [showDeleteAlert, setShowDeleteAlert] = useState(false);
	const [currPage, setCurrPage] = useState(PAGE.VIEW_EVENT);
	const [submitLoading, setSubmitLoading] = useState(false);

	useEffect(() => {
		if (isLessonRecord && eventProp.isLessonRecord) {
			reset(transformData(tempEvent));
		}
		return () => setEditable(false);
	}, [tempEvent]);

	useEffect(() => {
		setTempEvent(transformData(eventProp));
	}, [eventProp]);

	const selectedCourse = watch("course");

	const homeworkTime = watch("homeworkTime");

	const selectedStudents = watch("studentIds");

	const selectedLesson = watch("lesson");

	const { data: selectedStudentsData, isFetching: isStudentFetching } =
		useLeanStudentQuery(selectedStudents);

	const { data: studentCourseOptions, isFetching: isStudentCourseFetching } =
		useQuery(
			["studentCourse", selectedStudents],
			() => {
				const studentCourses = selectedStudentsData.map((student) =>
					student?.courses?.map((course) => ({
						...course.courseDetail,
						hoursLeft: course.hoursLeft,
					})),
				);
				setValue("lesson", undefined);
				const intersectedCourses = studentCourses
					.map((studentCourse) =>
						studentCourse.filter((course) =>
							studentCourses
								.map((studentCourse) =>
									studentCourse.some((course_1) => course_1.id === course.id),
								)
								.every((isTrue) => isTrue),
						),
					)
					.flat()
					.filter(
						(course, index, self) =>
							index === self.findIndex((course_1) => course_1.id === course.id),
					);
				return intersectedCourses.map((course) => ({
					label: `${course.name} [${course.hoursLeft}hours]`,
					value: course.id,
					data: course,
					toString: () => course.id,
				}));
			},
			{ enabled: !!selectedStudents?.length && !isStudentFetching },
		);

	const completedLessons = useMemo(
		() =>
			selectedStudentsData
				? selectedStudentsData?.reduce(
						(accumulated, student) => {
							const records = student?.lessonRecords
								?.filter(
									(lessonRecord) =>
										lessonRecord?.lessonDetail?.courseId !== null &&
										lessonRecord?.lessonDetail?.courseId !== undefined &&
										lessonRecord?.lessonDetail?.courseId ===
											selectedCourse?.id &&
										lessonRecord?.lessonDetail?.lessonId &&
										!lessonRecord?.lessonDetail?.lesson?.isCustomLesson,
								)
								.map((lessonRecord) => lessonRecord?.lessonDetail?.lesson);
							accumulated.ids = accumulated.ids.concat(
								records.map((record) => record.id),
							);
							accumulated.records = accumulated.records.concat(
								records.filter((lesson) => lesson.id !== selectedLesson?.id),
							);
							accumulated.customRecords = student?.lessonRecords
								?.filter(
									(lessonRecord) =>
										lessonRecord?.lessonDetail?.lessonId !==
											selectedLesson?.id &&
										lessonRecord?.lessonDetail?.lesson?.isCustomLesson &&
										lessonRecord?.lessonDetail?.lessonId,
								)
								.map((lessonRecord) => {
									return {
										...lessonRecord?.lessonDetail?.lesson,
										date: lessonRecord?.lessonDetail?.date,
									};
								});
							return accumulated;
						},
						{ ids: [], records: [], customRecords: [] },
					)
				: { ids: [], records: [], customRecords: [] },
		[selectedStudentsData, selectedLesson, selectedCourse?.id],
	);

	const legacyCompletedLessons = useMemo(
		() =>
			selectedStudentsData
				? selectedStudentsData?.reduce(
						(accumulated, student) => {
							accumulated.ids = accumulated.ids.concat(
								student?.legacyCompletedLessonRecords
									?.filter(
										(lessonRecord) =>
											lessonRecord?.lessonDetail?.courseId !== null &&
											lessonRecord?.lessonDetail?.courseId ===
												selectedCourse?.id,
									)
									.map((lessonRecord) => lessonRecord?.lessonId),
							);
							accumulated.records = accumulated.records.concat(
								student?.legacyCompletedLessonRecords
									.filter(
										(lessonRecord) =>
											lessonRecord?.lessonDetail?.courseId ===
											selectedCourse?.id,
									)
									.map((lessonRecord) => lessonRecord?.lesson),
							);
							return accumulated;
						},
						{ ids: [], records: [] },
					)
				: { ids: [], records: [] },
		[selectedStudentsData, selectedCourse?.id],
	);

	const { data: lessonOptions } = useQuery(
		["lesson", selectedCourse?.id, selectedLesson?.id],
		() =>
			apiService.course.get(selectedCourse?.id).then((course) => {
				const lessonsByCategory = lessonCategories.map((category) => ({
					label: capitalize(category),
					options: course.lessons
						.filter(
							(lesson) =>
								!legacyCompletedLessons.ids.includes(lesson.id) &&
								!completedLessons.ids.includes(lesson.id),
						)
						.map((lesson) =>
							selectedLesson?.id === lesson.id
								? {
										value: { ...lesson, toString: () => lesson.id },
										label: lesson.name,
										disabled: true,
									}
								: {
										value: { ...lesson, toString: () => lesson.id },
										label: lesson.name,
									},
						)
						.filter((lesson) => lesson?.value?.category === category),
				}));
				return lessonsByCategory;
			}),
		{
			enabled:
				!!selectedCourse?.id &&
				!isStudentCourseFetching &&
				!!selectedStudentsData &&
				selectedLesson !== undefined,
		},
	);

	const toggleEdit = () => {
		setEditable((prev) => !prev);
		reset(transformData(tempEvent));
	};

	const updateLessonRecord = handleSubmit((data) => {
		setSubmitLoading(true);
		const { lesson, course, teacher, observers } = data;
		const formData = {
			...data,
			...(lesson && { lessonId: lesson.id }),
			...(course && { courseId: course.id }),
			...(teacher && { teacherId: teacher.id }),
			...(Boolean(data.customlessonName) && {
				customLessonName: data.customLessonName,
			}),
			...(observers && {
				observerIds: observers.map((observer) => observer.id),
			}),
		};
		apiService.lessonRecord
			.update(tempEvent?.id, formData)
			.then((data) => {
				createNotification({
					type: "success",
					message: "Successfully edited lesson record",
				});
				parseDataToFormInput(data);
				refetchEvents();
				setEditable(false);
			})
			.catch((error) =>
				createNotification({
					type: "danger",
					message: `Failed to add lesson record: ${error.message}`,
				}),
			)
			.finally(() => setSubmitLoading(false));
	});

	const deleteLessonRecord = (deleteRecurred) =>
		apiService.lessonRecord
			.delete(tempEvent?.id, { delete_recurred: deleteRecurred })
			.then(() => {
				createNotification({
					type: "success",
					message: "Successfully delete lesson record",
				});
				refetchEvents();
				hide();
			})
			.finally(() => setShowDeleteAlert(false));

	if (!isLessonRecord)
		return (
			<LeaveOrOTDetailsPage
				open={open}
				hide={hide}
				event={tempEvent}
				register={register}
				editable={editable}
			/>
		);
	else if (currPage === PAGE.ADD_ATTENDANCE) {
		return (
			<AddAttendancePage
				open={open}
				hide={hide}
				event={tempEvent}
				setCurrPage={setCurrPage}
				refetchSingleLessonRecord={refetchSingleLessonRecord}
				refetchLessonRecords={refetchEvents}
			/>
		);
	} else
		return (
			<LessonDetailPage
				updateLessonRecord={updateLessonRecord}
				open={open}
				hide={hide}
				editable={editable}
				event={tempEvent}
				refetchSingleLessonRecord={refetchSingleLessonRecord}
				selectedStudentsData={selectedStudentsData}
				register={register}
				errors={errors}
				watch={watch}
				teacherOptions={teacherOptions}
				refetchEvents={refetchEvents}
				studentOptions={studentOptions}
				submitLoading={submitLoading}
				courseOptions={studentCourseOptions}
				selectedCourse={selectedCourse}
				lessonOptions={lessonOptions}
				isFetchingSingleLessonRecord={isFetchingSingleLessonRecord}
				control={control}
				dirtyFields={dirtyFields}
				setValue={setValue}
				deleteLessonRecord={deleteLessonRecord}
				toggleEdit={toggleEdit}
				setShowDeleteAlert={setShowDeleteAlert}
				showStudentReportModal={showStudentReportModal}
				showAddHomeworkModal={showAddHomeworkModal}
				reset={reset}
				getValues={getValues}
				views={["month, week, day"]}
				showDeleteAlert={showDeleteAlert}
				openAttendanceModal={() => setCurrPage(PAGE.ADD_ATTENDANCE)}
				isCustomLesson={watch("isCustomLesson")}
				setCustomLesson={(bool) => setValue("isCustomLesson", bool)}
				legacyCompletedLessons={legacyCompletedLessons}
				completedLessons={completedLessons}
				isStudentCourseFetching={isStudentCourseFetching}
			/>
		);
};

export default ViewEventModal;
